import React, { useEffect, useCallback, useState } from "react";
import NotificationPopup from "../../pages/NotificationPopup";
import PropTypes from "prop-types";
import {
  AppBar,
  Box,
  CssBaseline,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  Menu,
  MenuItem,
  Toolbar,
  Typography,
  Button,
  Badge,
  useTheme,
  useMediaQuery,
  Select,
  FormControl,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import NotificationsIcon from "@mui/icons-material/Notifications";
import { Link, useNavigate } from "react-router-dom";
import Cookies from "js-cookie";
import { jwtDecode } from "jwt-decode";
import apiService from "../ReasuableFuncations/api";
import { Languages } from "lucide-react";
import { Person } from '@mui/icons-material'; // Change this line

const drawerWidth = 240;

function DrawerAppBar({ window, children }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));

  const [mobileOpen, setMobileOpen] = useState(false);
  const [dashboardAnchorEl, setDashboardAnchorEl] = useState(null);
  const [analysisAnchorEl, setAnalysisAnchorEl] = useState(null);
  const [notifications, setNotifications] = useState(0);
  const [upcomingProjects, setUpcomingProjects] = useState([]);
  const [filteredRisks, setFilteredRisks] = useState([]);
  const [filteredReminders, setFilteredReminders] = useState([]);
  const [newsAlerts, setNewsAlerts] = useState([]);
  const [eventAlerts, setEventAlerts] = useState([]);
  const [error, setError] = useState("");
  const [popupOpen, setPopupOpen] = useState(false);
  const [userAnchorEl, setUserAnchorEl] = useState(null); // Rename this
  const [language, setLanguage] = useState('en'); // Add this state
  const [languageAnchorEl, setLanguageAnchorEl] = useState(null);
  const navigate = useNavigate();

  const checkTokenExpiration = useCallback(() => {
    const token = Cookies.get('accessToken') || localStorage.getItem('accessToken');

    if (!token) {
      console.log("No token found, logging out");
      signOut();
      return false;
    }

    try {
      const decodedToken = jwtDecode(token);
      const currentTime = Date.now() / 1000;

      if (decodedToken.exp < currentTime) {
        console.log("Token expired, logging out");
        signOut();
        return false;
      }

      return true;
    } catch (error) {
      console.error("Error decoding token:", error);
      signOut();
      return false;
    }
  }, []);

  const signOut = useCallback(async () => {
    try {
      await apiService.get("/api/logout");
    } catch (error) {
      console.error("Error during sign out:", error);
    } finally {
      localStorage.clear();
      Cookies.remove("accessToken");
      navigate("/");
    }
  }, [navigate]);

  const changePassword = () => {
    navigate('/change-password');
    setMobileOpen(false);
  };

  const Language = () => {
    navigate('/language');
    setMobileOpen(false);
  };

  const handleLanguageChange = (event) => {
    setLanguage(event.target.value);
    closeLanguageMenu();
  };

  const openLanguageMenu = (event) => setLanguageAnchorEl(event.currentTarget);
  const closeLanguageMenu = () => setLanguageAnchorEl(null);

  const toggleDrawer = () => setMobileOpen((prev) => !prev);
  const openDashboardMenu = (event) =>
    setDashboardAnchorEl(event.currentTarget);
  const closeDashboardMenu = () => setDashboardAnchorEl(null);
  const openAnalysisMenu = (event) => setAnalysisAnchorEl(event.currentTarget);
  const closeAnalysisMenu = () => setAnalysisAnchorEl(null);
  const openUserMenu = (event) => setUserAnchorEl(event.currentTarget); // Rename this
  const closeUserMenu = () => setUserAnchorEl(null); // Rename this

  // Token expiration check effect
  useEffect(() => {
    const tokenCheckInterval = setInterval(() => {
      checkTokenExpiration();
    }, 60000); // Check every minute

    // Initial check
    checkTokenExpiration();

    return () => clearInterval(tokenCheckInterval);
  }, [checkTokenExpiration]);

  // Data fetching effect
  useEffect(() => {
    const fetchData = async () => {
      if (!checkTokenExpiration()) return; // Exit if token is invalid

      try {
        const userData = JSON.parse(localStorage.getItem("user"));
        if (!userData) {
          setError("User data not found. Please sign in again.");
          navigate("/login");
          return;
        }

        const {
          selectedOption,
          regionalNetworkOption,
          memberStateOption,
          countryOption,
        } = userData;

        let projectUrl = "/api/projectalert";
        let riskAlertsUrl = "/api/riskalert";
        let reminderAlertsUrl = "/api/reminder";

        if (selectedOption === 'Regional Network' && regionalNetworkOption) {
          projectUrl = `${projectUrl}?regionalNetwork=${regionalNetworkOption}`;
        } else if (selectedOption === 'Member State' && memberStateOption) {
          projectUrl = `${projectUrl}?country=${countryOption}`;
        }

        // Fetch projects
        const projectResponse = await apiService.get(projectUrl);
        const projectData = projectResponse?.data || [];
        setUpcomingProjects(Array.isArray(projectData) ? projectData : []);
        const projectCount = Array.isArray(projectData)
          ? projectData.length
          : 0;

        // Fetch risk alerts
        const riskAlertsResponse = await apiService.get(riskAlertsUrl);
        const riskData = riskAlertsResponse?.data || [];
        setFilteredRisks(Array.isArray(riskData) ? riskData : []);
        const riskCount = Array.isArray(riskData) ? riskData.length : 0;

        // Fetch reminder alerts
        const reminderResponse = await apiService.get(reminderAlertsUrl);
        const reminderData = reminderResponse?.data || [];
        setFilteredReminders(Array.isArray(reminderData) ? reminderData : []);
        const reminderCount = Array.isArray(reminderData)
          ? reminderData.length
          : 0;

        // Fetch news alerts
        const newsResponse = await apiService.get("/api/newsalert");
        const newsData = newsResponse?.data || [];
        const newsCount = Array.isArray(newsData) ? newsData.length : 0;
        setNewsAlerts(newsData);

        // Fetch event alerts
        const eventResponse = await apiService.get("/api/eventalert");
        const eventData = eventResponse?.data || [];
        const eventCount = Array.isArray(eventData) ? eventData.length : 0;
        setEventAlerts(eventData);

        // Calculate total notifications
        const countNotifications = projectCount + riskCount + reminderCount + newsCount + eventCount;
        setNotifications(countNotifications);
      } catch (error) {
        console.error('Error fetching data:', error);
        setError('Failed to fetch data. Please try again later.');

        // If error is due to authentication, sign out
        if (error.response?.status === 401) {
          signOut();
        }
      }
    };

    fetchData();
    const intervalId = setInterval(fetchData, 300000); // Check every 5 minutes
    return () => clearInterval(intervalId);
  }, [checkTokenExpiration, navigate, signOut]);

  // Navigation items
  const menuItems = [
    { text: "Home", path: "/Home" },
    { text: "Map", path: "/africa-map" },
    { text: "Api-Key", path: "/apikeygenerator" },
  ];

  const dashboardMenuItems = [
    { text: "Data Dashboard", path: "/sdata-collection" },
    { text: "Risk Dashboard", path: "/risk-dashboard" },
    { text: "Project Dashboard", path: "/project-list" },
    { text: "Stakeholder Dashboard", path: "/stakeholder-dashboard" },
    { text: "Budget Dashboard", path: "/budget-dashboard" },
    // { text: "Climate Dashboard", path: "/climate-dashboard" },
    { text: "News and Events", path: "/new-event" },
    { text: "ICT Statistics", path: "/ict-statistics-dashboard" },
  ];

  const analysisMenuItems = [
    { text: 'Data Analysis', path: '/dataanalysis' },
    { text: 'Risk Analysis', path: '/risksnalysis' },
    { text: 'Budget Analysis', path: '/budget-analysis' },
    { text: 'Project Analysis', path: '/project-analysis' }
  ];

  // Drawer content
  const drawerContent = (
    <Box onClick={toggleDrawer} sx={{ textAlign: "center" }}>
      <Typography variant="h6" sx={{ my: 2, fontWeight: "bold" }}>
        Portal
      </Typography>
      <Divider />
      <List sx={{ padding: 0 }}>
        {menuItems.map((item) => (
          <ListItemButton
            key={item.text}
            sx={{
              textAlign: 'center',
              py: 1.5,
              "&:hover": { backgroundColor: "#f5f5f5" },
            }}
            component={Link}
            to={item.path}
          >
            {item.text}
          </ListItemButton>
        ))}
        <ListItemButton
          sx={{
            textAlign: 'center',
            py: 1.5,
            '&:hover': { backgroundColor: '#f5f5f5' }
          }}
          onClick={openDashboardMenu}
        >
          Dashboards
        </ListItemButton>
        <ListItemButton
          sx={{
            textAlign: 'center',
            py: 1.5,
            '&:hover': { backgroundColor: '#f5f5f5' }
          }}
          onClick={openAnalysisMenu}
        >
          Analysis
        </ListItemButton>
        <Divider />
        <ListItemButton
          sx={{
            textAlign: 'center',
            py: 1.5,
            '&:hover': { backgroundColor: '#f5f5f5' }
          }}
          onClick={changePassword}
        >
          Change Password
        </ListItemButton>
        <ListItemButton
          sx={{
            textAlign: 'center',
            py: 1.5,
            '&:hover': { backgroundColor: '#f5f5f5' }
          }}
          onClick={Language}
        >
          Select Language
        </ListItemButton>
        <ListItemButton
          sx={{
            textAlign: 'center',
            py: 1.5,
            '&:hover': { backgroundColor: '#f5f5f5' }
          }}
          onClick={signOut}
        >
          Log out
        </ListItemButton>
      </List>
    </Box>
  );

  const container = window ? () => window().document.body : undefined;

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />
      <AppBar
        component="nav"
        sx={{
          zIndex: theme.zIndex.drawer + 1,
        }}
      >
        <Toolbar sx={{
          justifyContent: 'space-between',
          minHeight: { xs: '56px', sm: '64px' },
          padding: { xs: '0 8px', sm: '0 16px' }
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {(isMobile || isTablet) && (
              <IconButton
                color="inherit"
                aria-label="open drawer"
                edge="start"
                onClick={toggleDrawer}
                sx={{ mr: 1 }}
              >
                <MenuIcon />
              </IconButton>
            )}
            <Button
              variant="h6"
              sx={{
                fontWeight: 'bold',
                color: 'white',
                fontSize: { xs: '1.1rem', sm: '1.25rem' }
              }}
              component={Link}
              to="/home"
            >
              Portal
            </Button>
          </Box>

          {!isMobile && !isTablet && (
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}>
              {menuItems.map((item) => (
                <Button
                  key={item.text}
                  sx={{
                    color: '#fff',
                    padding: '6px 12px',
                    minWidth: 'auto'
                  }}
                  component={Link}
                  to={item.path}
                >
                  {item.text}
                </Button>
              ))}
              <Button
                sx={{ color: '#fff' }}
                onClick={openDashboardMenu}
              >
                Dashboards
              </Button>
              <Button
                sx={{ color: '#fff' }}
                onClick={openAnalysisMenu}
              >
                Analysis
              </Button>
              <IconButton
                color="inherit"
                onClick={openUserMenu}
                sx={{ ml: 1 }}
              >
                <Person />
              </IconButton>
            </Box>
          )}

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {/* <FormControl size="small" sx={{ minWidth: 100 }}>
              <Select
                value={language}
                onChange={handleLanguageChange}
                variant="standard"
                sx={{ 
                  color: 'white',
                  '&:before': { borderColor: 'white' },
                  '&:after': { borderColor: 'white' },
                  '& .MuiSvgIcon-root': { color: 'white' }
                }}
              >
               <MenuItem value="en">English</MenuItem>
               <MenuItem value="fr">French</MenuItem>
               <MenuItem value="ar">Arabic</MenuItem>
               <MenuItem value="pt">Portuguese</MenuItem>

              </Select>
            </FormControl> */}

            <IconButton
              color="inherit"
              onClick={() => setPopupOpen(true)}
            >
              <Badge
                badgeContent={notifications}
                color="error"
                sx={{
                  "& .MuiBadge-badge": {
                    backgroundColor: "#ff0000",
                    color: "white",
                  },
                }}
              >
                <NotificationsIcon />
              </Badge>
            </IconButton>

            {/* <IconButton
              color="inherit"
              onClick={openUserMenu}
            >
              <Person />
            </IconButton> */}
          </Box>
        </Toolbar>
      </AppBar>

      <Drawer
        container={container}
        variant="temporary"
        anchor="left"
        open={mobileOpen}
        onClose={toggleDrawer}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: drawerWidth,
            maxWidth: "80vw",
          },
        }}
      >
        {drawerContent}
      </Drawer>

      <Menu
        anchorEl={dashboardAnchorEl}
        open={Boolean(dashboardAnchorEl)}
        onClose={closeDashboardMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
        sx={{
          "& .MuiPaper-root": {
            maxHeight: "70vh",
            width: { xs: "90vw", sm: "auto" },
          },
        }}
      >
        {dashboardMenuItems.map((item) => (
          <MenuItem
            key={item.text}
            onClick={() => {
              navigate(item.path);
              closeDashboardMenu();
            }}
            sx={{
              py: 1.5,
              px: { xs: 2, sm: 3 },
            }}
          >
            {item.text}
          </MenuItem>
        ))}
      </Menu>

      <Menu
        anchorEl={analysisAnchorEl}
        open={Boolean(analysisAnchorEl)}
        onClose={closeAnalysisMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
        sx={{
          "& .MuiPaper-root": {
            maxHeight: "70vh",
            width: { xs: "90vw", sm: "auto" },
          },
        }}
      >
        {analysisMenuItems.map((item) => (
          <MenuItem
            key={item.text}
            onClick={() => {
              navigate(item.path);
              closeAnalysisMenu();
            }}
            sx={{
              py: 1.5,
              px: { xs: 2, sm: 3 },
            }}
          >
            {item.text}
          </MenuItem>
        ))}
      </Menu>

      <Menu
        anchorEl={userAnchorEl}
        open={Boolean(userAnchorEl)}
        onClose={closeUserMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        sx={{
          "& .MuiPaper-root": {
            width: { xs: "90vw", sm: "200px" },
          },
        }}
      >
        <MenuItem
          onClick={() => {
            changePassword();
            closeUserMenu();
          }}
          sx={{ py: 1.5 }}
        >
          Change Password
        </MenuItem>
        {/* <MenuItem
          onClick={openLanguageMenu}
          sx={{ py: 1.5 }}
        >
          Select Language
        </MenuItem> */}
        <MenuItem
          onClick={() => {
            signOut();
            closeUserMenu();
          }}
          sx={{ py: 1.5 }}
        >
          Log out
        </MenuItem>
      </Menu>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 2, sm: 3 },
          width: "100%",
          marginTop: { xs: "56px", sm: "64px" },
        }}
      >
        {children}
      </Box>

      {popupOpen && (
        <NotificationPopup
          upcomingProjects={upcomingProjects}
          filteredRisks={filteredRisks}
          filteredReminders={filteredReminders}
          newsAlerts={newsAlerts}
          eventAlerts={eventAlerts}
          setPopupOpen={setPopupOpen}
        />
      )}
    </Box>
  );
}

DrawerAppBar.propTypes = {
  window: PropTypes.func,
  children: PropTypes.node,
};

export default DrawerAppBar;
